import React, { useMemo, useState } from 'react';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';
import { TooltipIcon, CheckmarkIcon, ChevronLeftIcon, ChevronRightIcon } from '../icons';

type MedicationSafetyCheckpointProps = {
  stepIndex: number;
  totalSteps: number;
  progressLabel: string;
  title: string;
  subtitle: string;
  detailHeading?: string;
  detailBody?: string;
  acknowledgements: Array<{ id: string; label: string }>;
  onBack: () => void;
  onContinue: () => void;
};

const riskBullets = [
  'Affect how psychedelics work in the body',
  'Increase the risk of adverse reactions',
  'Impact emotional or physiological responses during a journey'
];

const matchingBullets = [
  'You will only be matched with guides who have the specific training, experience, and protocols to support clients on psychiatric medications.',
  'Not all guides are able to work safely with medicated clients.',
  'Your matches may be fewer, but they will be safer and more appropriate for your needs.'
];

const disclaimer = 'Guides Collective does not provide medical advice, safety screening, risk assessment, or health-related recommendations. If you have questions or concerns related to medications, mental health, or physical health, you should consult a qualified healthcare professional. Guides operate independently, and users are responsible for making their own health decisions.';

export function MedicationSafetyCheckpoint({
  stepIndex,
  totalSteps,
  progressLabel,
  title,
  subtitle,
  detailHeading,
  detailBody,
  acknowledgements,
  onBack,
  onContinue
}: MedicationSafetyCheckpointProps) {
  const progress = totalSteps > 0 ? ((stepIndex + 1) / totalSteps) * 100 : 0;
  const [acks, setAcks] = useState<Record<string, boolean>>({});
  const allAcknowledged = useMemo(
    () => acknowledgements.every(item => acks[item.id]),
    [acks, acknowledgements]
  );

  const handleAcknowledgeToggle = (id: string) => {
    setAcks(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-onboarding-content-bg text-onboarding-text-dark">
        {/* Mobile header */}
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#cccccc]/60 bg-onboarding-header px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        {/* Desktop header */}
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        {/* Content wrapper */}
        <div className="flex flex-1 flex-col lg:px-[72px] lg:py-10 lg:gap-10">
          {/* Progress bar */}
          <section className="flex shrink-0 flex-col gap-[6px] px-4 lg:px-0 pb-2 pt-4 lg:pt-0 lg:pb-0">
            <span className="font-helvetica text-sm leading-[1.6] text-onboarding-primary">{progressLabel}</span>
            <div className="h-2 w-full overflow-hidden rounded-full bg-[#c9c9c9]">
              <div
                className="h-full rounded-full bg-onboarding-accent transition-[width] duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </section>

          {/* Main content - centered container */}
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center overflow-y-auto lg:overflow-visible px-4 lg:px-0 pb-10 lg:pb-0 pt-6 lg:pt-0">
            <div className="w-full max-w-[509px] flex flex-col gap-6">
              {/* Title section */}
              <section className="flex flex-col gap-2">
                <h1 className="font-recoleta text-[32px] font-normal leading-[1.1] text-onboarding-card-green">
                  {title}
                </h1>
                <p className="font-helvetica text-lg leading-[1.4] text-black">
                  {subtitle}
                </p>
              </section>

              {/* Acknowledgements section - BEFORE tooltips per Figma */}
              <section className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h2 className="font-helvetica text-lg font-medium leading-[1.4] text-black">
                    Please acknowledge the following
                  </h2>
                  <p className="font-helvetica text-sm leading-[1.6] text-[#707c8b]">
                    To proceed, please confirm all three statement below.
                  </p>
                </div>

                <div className="flex flex-col gap-6">
                  {acknowledgements.map(item => (
                    <label
                      key={item.id}
                      className="flex cursor-pointer items-start gap-3"
                      htmlFor={item.id}
                    >
                      <input
                        id={item.id}
                        type="checkbox"
                        checked={Boolean(acks[item.id])}
                        onChange={() => handleAcknowledgeToggle(item.id)}
                        className="sr-only"
                      />
                      <div className="flex items-center py-[3px] shrink-0">
                        <span
                          aria-hidden
                          className={`flex h-6 w-6 items-center justify-center rounded-[6px] border transition ${
                            acks[item.id]
                              ? 'border-onboarding-card-green bg-onboarding-card-green'
                              : 'border-onboarding-radio-border'
                          }`}
                        >
                          {acks[item.id] && (
                            <CheckmarkIcon className="h-3 w-3 text-white" />
                          )}
                        </span>
                      </div>
                      <span className="font-helvetica text-lg leading-[1.4] tracking-[-0.09px] text-black">
                        {item.label}
                      </span>
                    </label>
                  ))}
                </div>
              </section>

              {/* Risk info tooltip */}
              <div className="flex items-start gap-[10px] rounded-2xl border border-onboarding-card-green px-4 py-3 min-h-[48px] overflow-clip">
                <div className="shrink-0 h-[21px] flex items-center">
                  <TooltipIcon className="text-[#CA9D35]" />
                </div>
                <div className="flex flex-1 flex-col gap-1 text-onboarding-card-green">
                  <p className="font-helvetica text-base font-bold leading-[1.6]">
                    Some psychiatric medications can:
                  </p>
                  <div className="font-helvetica text-sm font-normal leading-[1.4]">
                    <ul className="list-disc mb-0 ml-[21px] space-y-0">
                      {riskBullets.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    <p className="mt-3">
                      Your safety is our absolute priority. We want you to have all the facts as you move forward.
                    </p>
                  </div>
                </div>
              </div>

              {/* Matching info tooltip */}
              <div className="flex flex-col gap-[10px] rounded-2xl border border-onboarding-card-green px-4 py-3 min-h-[48px] overflow-clip">
                <div className="flex items-start gap-[10px]">
                  <div className="shrink-0 h-[21px] flex items-center">
                    <TooltipIcon className="text-[#CA9D35]" />
                  </div>
                  <div className="flex flex-1 flex-col gap-1 text-onboarding-card-green">
                    <p className="font-helvetica text-base font-bold leading-[1.6]">
                      {detailHeading || 'How this affects your matching'}
                    </p>
                    <div className="font-helvetica flex flex-col gap-2 text-sm font-normal leading-[1.4]">
                      {detailBody && (
                        <p>{detailBody}</p>
                      )}
                      <div>
                        <ul className="list-disc mb-0 ml-[21px] space-y-0">
                          {matchingBullets.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                        <p className="mt-3">
                          We appreciate your honesty — it helps us match you safely and responsibly.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="w-full">
                <div className="flex items-center justify-center gap-3 py-4">
                  <button
                    type="button"
                    onClick={onBack}
                    aria-label="Go back"
                    className="flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full border border-onboarding-card-green bg-transparent transition-colors hover:bg-onboarding-card-green/5"
                  >
                    <ChevronLeftIcon className="text-onboarding-card-green" />
                  </button>
                  <button
                    type="button"
                    onClick={onContinue}
                    disabled={!allAcknowledged}
                    className="flex flex-1 h-[54px] items-center justify-center gap-2 rounded-full bg-onboarding-card-green px-5 py-[18px] font-helvetica text-base leading-[1.6] tracking-[-0.08px] text-onboarding-content-bg transition-colors hover:bg-onboarding-card-green/90 disabled:pointer-events-none disabled:opacity-50"
                  >
                    <span>Continue</span>
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>

              {/* Disclaimer */}
              <p className="font-helvetica text-sm leading-[1.6] text-black">
                {disclaimer}
              </p>
            </div>
          </main>
        </div>
      </div>

      {/* Site-wide footer - only visible on large screens */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
}
